// Precios por lista y clasificación del producto (pedido de Rodrigo, 2026-10-13: «complicado ver
// un precio, cambiar un precio; no veo súper familia, familia, subfamilia y marca»). Contratos de
// /precios/listas/:id/tratamiento, /productos/:id/precios y el árbol /catalogo/categorias.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';

/** Tramo por cantidad tal como lo guarda el ERP: [desde, hasta) — `hasta` null = «y más». */
export interface TramoApi {
  desde: string;
  hasta: string | null;
  valor: string;
}

export interface PrecioVigente {
  precioId: string;
  valor: string;
  desde: string | null;
  escalas: TramoApi[];
}

export interface PresentacionConPrecio {
  presentacionId: string;
  codigo: string;
  nombre: string;
  factor: string;
  esBase: boolean;
  precio: PrecioVigente | null;
}

export interface FilaTratamiento {
  productoId: string;
  sku: string;
  nombre: string;
  estado: string;
  categoriaId: string | null;
  marcaId: string | null;
  presentaciones: PresentacionConPrecio[];
}

export interface ListaPrecio {
  id: string;
  codigo: string;
  nombre: string;
  uso: string;
  semantica: string;
  activa: boolean;
  empresa_id: string;
}

export interface PreciosDeProductoEnLista {
  listaId: string;
  codigo: string;
  nombre: string;
  uso: string;
  semantica: string;
  precios: Array<{
    precioId: string;
    presentacionId: string;
    valor: string;
    desde: string | null;
    escalas: TramoApi[];
  }>;
}

export interface CategoriaArbol {
  id: string;
  padre_id: string | null;
  codigo: string;
  nombre: string;
  nivel: number;
  activa: boolean;
  version: number;
}

export interface MarcaFila {
  id: string;
  codigo: string;
  nombre: string;
  activa: boolean;
  version: number;
}

/** Cómo se llama cada nivel del árbol (los nombres de Random). */
export const NIVELES = ['Súper familia', 'Familia', 'Subfamilia'] as const;
export const nombreNivel = (nivel: number) => NIVELES[nivel - 1] ?? `Nivel ${nivel}`;

export function useListasDeEmpresa(empresaId: string) {
  return useQuery({
    queryKey: ['precios', 'listas', empresaId],
    queryFn: async () =>
      (await api.get<ListaPrecio[]>('/precios/listas')).filter(
        (l) => l.empresa_id === empresaId && l.activa,
      ),
    staleTime: 5 * 60_000,
  });
}

export function useTratamiento(
  empresaId: string,
  listaId: string | null,
  f: { categoriaId?: string; marcaId?: string },
) {
  return useQuery({
    queryKey: ['precios', 'tratamiento', empresaId, listaId, f.categoriaId ?? '', f.marcaId ?? ''],
    queryFn: () =>
      api.get<FilaTratamiento[]>(`/precios/listas/${listaId}/tratamiento`, {
        query: { empresaId, categoriaId: f.categoriaId, marcaId: f.marcaId },
      }),
    enabled: !!listaId,
  });
}

export function usePreciosDeProducto(empresaId: string, productoId: string) {
  return useQuery({
    queryKey: ['precios', 'producto', empresaId, productoId],
    queryFn: () =>
      api.get<PreciosDeProductoEnLista[]>(`/productos/${productoId}/precios`, {
        query: { empresaId },
      }),
  });
}

export function useArbolCategorias() {
  return useQuery({
    queryKey: ['catalogo', 'categorias'],
    queryFn: () => api.get<CategoriaArbol[]>('/catalogo/categorias'),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useMarcasFila() {
  return useQuery({
    queryKey: ['catalogo', 'marcas'],
    queryFn: () => api.get<MarcaFila[]>('/catalogo/marcas'),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** El camino de una categoría desde la raíz: [súper familia, familia, subfamilia]. */
export function caminoDe(
  categoriaId: string | null | undefined,
  porId: ReadonlyMap<string, CategoriaArbol>,
): CategoriaArbol[] {
  const camino: CategoriaArbol[] = [];
  let c = categoriaId ? porId.get(categoriaId) : undefined;
  while (c && camino.length < 8) {
    camino.unshift(c);
    c = c.padre_id ? porId.get(c.padre_id) : undefined;
  }
  return camino;
}

export function hijasDe(categorias: readonly CategoriaArbol[], padreId: string | null) {
  return categorias
    .filter((c) => (c.padre_id ?? null) === padreId)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}

// ---------------------------------------------------------------- escalas legibles

/** Tramo como se edita: desde qué cantidad rige qué precio (el último queda abierto). */
export interface TramoEdicion {
  desde: string;
  valor: string;
}

export const aEdicion = (t: readonly TramoApi[]): TramoEdicion[] =>
  [...t]
    .sort((a, b) => Number(a.desde) - Number(b.desde))
    .map((x) => ({ desde: String(Number(x.desde)), valor: String(Number(x.valor)) }));

/** De vuelta al formato del ERP: el «hasta» de cada tramo es el «desde» del siguiente. */
export function aApi(t: readonly TramoEdicion[]) {
  const orden = [...t].sort((a, b) => Number(a.desde) - Number(b.desde));
  return orden.map((x, i) => ({
    cantidadDesde: String(Number(x.desde)),
    ...(orden[i + 1] ? { cantidadHasta: String(Number(orden[i + 1]!.desde)) } : {}),
    valor: String(Number(x.valor)),
  }));
}

const miles = (n: number) => n.toLocaleString('es-CL', { maximumFractionDigits: 2 });

/** «1–23: $1.495 · 24+: $1.375» (en la unidad de la presentación). */
export function textoEscalas(t: readonly TramoApi[]): string {
  return [...t]
    .sort((a, b) => Number(a.desde) - Number(b.desde))
    .map((x) => {
      const lo = Math.max(1, Number(x.desde));
      const rango =
        x.hasta === null
          ? `${miles(lo)}+`
          : Number.isInteger(Number(x.hasta))
            ? `${miles(lo)}–${miles(Number(x.hasta) - 1)}`
            : `${miles(lo)}–${miles(Number(x.hasta))}`;
      return `${rango}: $${miles(Number(x.valor))}`;
    })
    .join(' · ');
}

/** Precio con un ajuste porcentual, redondeado a peso (las listas de MADECI no usan decimales). */
export const ajustar = (valor: string, pct: number) =>
  String(Math.max(0, Math.round(Number(valor) * (1 + pct / 100))));

/**
 * Texto digitado → número que acepta el ERP. «1.495» y «$1.495» = 1495 (punto de miles);
 * «1495,5» = 1495.5 (coma decimal); «1495.5» también, porque no calza con grupos de miles.
 */
export function valorDigitado(texto: string): string | null {
  let limpio = texto.trim().replace(/[$\s]/g, '');
  if (limpio.includes(',')) limpio = limpio.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(limpio)) limpio = limpio.replace(/\./g, '');
  return /^\d{1,14}(\.\d{1,4})?$/.test(limpio) ? String(Number(limpio)) : null;
}

/**
 * Homologa unidad ↔ caja (pedido de Rodrigo, 2026-10-13): cambiar uno fija el otro POR EL FACTOR,
 * proporcional exacto: caja = unidad × factor; unidad = caja ÷ factor (redondeado a peso).
 */
export function homologar(nuevo: string, factorEditado: string, factorOtro: string): string {
  return String(
    Math.max(0, Math.round((Number(nuevo) * Number(factorOtro)) / Number(factorEditado))),
  );
}
